import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Clock } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { projects } from '../data/projects';
import { articles } from '../data/articles';
import { trackEvent } from '../analytics';

const Footer = () => {
  const { t, path } = useLanguage();
  const hasProjects = projects.some((p) => p.published);
  const hasArticles = articles.some((a) => a.published);
  const footerStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    borderTop: '1px solid #3a3a3a',
    marginTop: 'auto'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '3rem 1.5rem'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '2rem'
  };

  const logoSectionStyle = {
    gridColumn: 'span 1'
  };

  const logoStyle = {
    height: '2.5rem',
    width: 'auto',
    marginBottom: '1rem'
  };

  const companyDescStyle = {
    color: '#9ca3af',
    fontSize: '0.875rem',
    lineHeight: '1.6',
    marginBottom: '1rem',
    textAlign: 'left'
  };

  const copyrightStyle = {
    color: '#6b7280',
    fontSize: '0.75rem',
    textAlign: 'left'
  };

  const sectionTitleStyle = {
    color: 'white',
    fontWeight: '600',
    marginBottom: '1rem',
    textAlign: 'left'
  };

  const linkListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  };

  const listItemStyle = {
    textAlign: 'left'
  };

  const linkStyle = {
    color: '#9ca3af',
    textDecoration: 'none',
    fontSize: '0.875rem',
    transition: 'color 0.3s',
    textAlign: 'left'
  };

  const serviceListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  };

  const serviceItemStyle = {
    color: '#9ca3af',
    fontSize: '0.875rem',
    textAlign: 'left'
  };

  const contactItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '0.75rem'
  };

  const iconStyle = {
    width: '1rem',
    height: '1rem',
    color: '#dfa926',
    marginTop: '0.125rem',
    marginRight: '0.5rem',
    flexShrink: 0
  };

  const contactTextStyle = {
    color: '#9ca3af',
    fontSize: '0.875rem',
    textAlign: 'left'
  };

  const emailLinkStyle = {
    color: '#9ca3af',
    textDecoration: 'none',
    fontSize: '0.875rem',
    transition: 'color 0.3s',
    textAlign: 'left'
  };

  return (
    <footer style={footerStyle}>
      <div style={containerStyle}>
        <div style={gridStyle}>
          {/* Company Info */}
          <div style={logoSectionStyle}>
            <img 
              src="/logo.png" 
              alt="Hestia Doors Logo"
              style={logoStyle}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML = '<div style="color: #dfa926; font-size: 1.5rem; font-weight: bold; margin-bottom: 1rem;">HESTIA DOORS</div>';
              }}
            />
            <p style={companyDescStyle}>
              {t('footer.desc')}
            </p>
            <p style={copyrightStyle}>
              © {new Date().getFullYear()} Hestia Doors OÜ. {t('footer.rights')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={sectionTitleStyle}>{t('footer.quickLinks')}</h4>
            <ul style={linkListStyle}>
              {[
                [t('nav.home'), '/'],
                [t('nav.products'), '/products'],
                [t('categories.exteriorDoors.h1'), '/exterior-doors'],
                [t('categories.outwardDoors.h1'), '/outward-opening-exterior-doors'],
                [t('categories.inwardDoors.h1'), '/inward-opening-exterior-doors'],
                [t('categories.customDoors.h1'), '/custom-exterior-doors'],
                [t('categories.prefabDoors.h1'), '/exterior-doors-for-prefabricated-houses'],
                [t('categories.storageDoors.h1'), '/storage-doors'],
                ...(hasProjects ? [[t('projects.title'), '/projects']] : []),
                ...(hasArticles ? [[t('knowledge.title'), '/knowledge']] : []),
                [t('nav.about'), '/about'],
                [t('nav.services'), '/services'],
                [t('nav.quality'), '/quality'],
                [t('nav.downloads'), '/downloads'],
                [t('nav.contact'), '/contact']
              ].map(([label, to]) => (
                <li key={to} style={listItemStyle}>
                  <Link
                    to={path(to)}
                    style={linkStyle}
                    onMouseEnter={(e) => e.target.style.color = '#dfa926'}
                    onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 style={sectionTitleStyle}>{t('footer.servicesTitle')}</h4>
            <ul style={serviceListStyle}>
              {t('footer.services').map((service, i) => (
                <li key={i} style={serviceItemStyle}>{service}</li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 style={sectionTitleStyle}>{t('footer.contactTitle')}</h4>
            <div>
              <div style={contactItemStyle}>
                <MapPin style={iconStyle} />
                <div style={contactTextStyle}>
                  <p style={{ margin: 0 }}>Päikese 1</p>
                  <p style={{ margin: 0 }}>30221 Kohtla-Järve</p>
                  <p style={{ margin: 0 }}>Estonia</p>
                </div>
              </div>
              <div style={contactItemStyle}>
                <Mail style={iconStyle} />
                <a
                  href="mailto:info@hestiadoors.com"
                  onClick={() => trackEvent('email_click', { location: 'footer' })}
                  style={emailLinkStyle}
                  onMouseEnter={(e) => e.target.style.color = '#dfa926'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  info@hestiadoors.com
                </a>
              </div>
              <div style={contactItemStyle}>
                <Clock style={iconStyle} />
                <div style={contactTextStyle}>
                  <p style={{ margin: 0 }}>{t('contact.hoursWeekday')}</p>
                  <p style={{ margin: 0 }}>{t('contact.hoursSaturday')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
